// ─── Easing Functions ────────────────────────────────────────────────────────

/**
 * Standard easing functions for travel animations.
 * All take t ∈ [0, 1] and return eased t ∈ [0, 1].
 */
export const EASING_FNS = {
  linear: (t) => t,
  easeIn: (t) => t * t,
  easeOut: (t) => t * (2 - t),
  easeInOut: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
};

// ─── Quadratic Bézier ────────────────────────────────────────────────────────

/**
 * Evaluate a quadratic Bézier curve at time t (0–1).
 * Goes exactly from start (t=0) to end (t=1) through control point.
 * No overshoot past endpoints.
 *
 * @param {{start: {x,y}, control: {x,y}, end: {x,y}}} pathData
 * @param {number} t - Progress (0–1)
 * @returns {{x: number, y: number}}
 */
export function evaluatePath(pathData, t) {
  const ct = Math.max(0, Math.min(1, t));
  const inv = 1 - ct;
  return {
    x: inv * inv * pathData.start.x + 2 * inv * ct * pathData.control.x + ct * ct * pathData.end.x,
    y: inv * inv * pathData.start.y + 2 * inv * ct * pathData.control.y + ct * ct * pathData.end.y,
  };
}

// ─── Arc Computation ─────────────────────────────────────────────────────────

/**
 * Compute Bézier waypoints for a vertical arc between two points.
 * Control point is at horizontal midpoint, lifted above the highest point.
 *
 * @param {{x,y}} start
 * @param {{x,y}} end
 * @param {number} symbolHeight - Used to scale arc height
 * @param {object} [options]
 * @param {number} [options.arcHeightMultiplier=0.7] - Arc lift as fraction of symbolHeight
 * @returns {{start, control, end}} Path data for evaluatePath()
 */
export function computeArc(start, end, symbolHeight, options = {}) {
  const arcHeightMultiplier = options.arcHeightMultiplier ?? 0.7;
  const midX = (start.x + end.x) / 2;
  const arcHeight = symbolHeight * arcHeightMultiplier;
  const peakY = Math.min(start.y, end.y) - arcHeight;

  return {
    start,
    control: { x: midX, y: peakY },
    end,
  };
}

/**
 * Compute Bézier waypoints with a lateral offset for the control point.
 * Used when items arc in a specific direction (left or right).
 *
 * @param {{x,y}} start
 * @param {{x,y}} end
 * @param {number} symbolWidth - Used to scale lateral offset
 * @param {number} symbolHeight - Used to scale arc height
 * @param {"left"|"right"} direction - Which side to shift the arc
 * @param {number} [lateralOffset=0.5] - Lateral offset as fraction of symbolWidth
 * @param {object} [options]
 * @param {number} [options.arcHeightMultiplier=0.7] - Arc lift as fraction of symbolHeight
 * @returns {{start, control, end}} Path data for evaluatePath()
 */
export function computeDirectionalArc(start, end, symbolWidth, symbolHeight, direction, lateralOffset = 0.5, options = {}) {
  const arcHeightMultiplier = options.arcHeightMultiplier ?? 0.7;
  const midX = (start.x + end.x) / 2;
  const arcHeight = symbolHeight * arcHeightMultiplier;
  const peakY = Math.min(start.y, end.y) - arcHeight;

  const offset = symbolWidth * lateralOffset;
  const controlX = direction === "left"
    ? midX - offset
    : midX + offset;

  return {
    start,
    control: { x: controlX, y: peakY },
    end,
  };
}

